import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * UTF-8 BOM付きCSVファイルをダウンロードする
 *
 * @param csvContent CSV文字列（バックエンドからUTF-8 BOM付きで返される）
 * @param filename ダウンロードファイル名
 * @param delay ダウンロード遅延時間（ms）デフォルト: 0
 */
export function downloadCsvWithBom(csvContent: string, filename: string, delay: number = 0): void {
  const downloadFile = () => {
    // バックエンドからUTF-8 BOM付きのCSV文字列が返されるため、
    // そのままBlobに設定してUTF-8 BOMを保持
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8',
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;

    // DOM操作を最小限に
    document.body.appendChild(link);
    link.click();

    // クリーンアップ
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  };

  if (delay > 0) {
    setTimeout(downloadFile, delay);
  } else {
    downloadFile();
  }
}

/**
 * Base64エンコードされたZIPファイルをダウンロードする
 *
 * @param base64ZipContent Base64エンコードされたZIPファイル
 * @param filename ダウンロードファイル名
 * @param delay ダウンロード遅延時間（ms）デフォルト: 0
 */
export function downloadZipFromBase64(
  base64ZipContent: string,
  filename: string,
  delay: number = 0,
): void {
  const downloadFile = () => {
    try {
      // Base64デコード
      const binaryString = atob(base64ZipContent);
      const bytes = new Uint8Array(binaryString.length);

      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // ZIPファイル用のBlob作成
      const blob = new Blob([bytes], {
        type: 'application/zip',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;

      // DOM操作を最小限に
      document.body.appendChild(link);
      link.click();

      // クリーンアップ
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error('ZIPファイルのダウンロードエラー:', error);
      throw new Error('ZIPファイルのダウンロードに失敗しました');
    }
  };

  if (delay > 0) {
    setTimeout(downloadFile, delay);
  } else {
    downloadFile();
  }
}

// 環境変数に基づいてヘッダーの色を取得する関数
export function getHeaderColor(): string {
  const environment = import.meta.env.VITE_APP_ENV || 'local';

  switch (environment) {
    case 'prod':
      return '#2e7d32'; // 緑色
    case 'fkdev':
      return '#1976d2'; // 青色（現在の色）
    case 'local':
      return '#d32f2f'; // 赤色
    default:
      return '#1976d2'; // デフォルトは青色
  }
}

// 環境変数に基づいてヘッダーの色名を取得する関数（デバッグ用）
export function getEnvironmentName(): string {
  const environment = import.meta.env.VITE_APP_ENV || 'local';

  switch (environment) {
    case 'prod':
      return '本番環境';
    case 'fkdev':
      return '開発環境';
    case 'local':
      return 'ローカル環境';
    default:
      return '不明な環境';
  }
}

/**
 * ユーティリティ関数
 */

/**
 * CSVダウンロード用のユーティリティ関数
 * UTF-8 BOM付きでCSVファイルをダウンロード
 */
export function downloadCSV(data: any[], filename: string): void {
  // BOM（Byte Order Mark）を追加
  const BOM = '\uFEFF';

  // ヘッダー行を作成
  const headers = Object.keys(data[0] || {});
  const csvContent = [
    headers.join(','),
    ...data.map((row) =>
      headers
        .map((header) => {
          const value = row[header];
          // 値にカンマ、改行、ダブルクォートが含まれる場合はダブルクォートで囲む
          if (
            typeof value === 'string' &&
            (value.includes(',') || value.includes('\n') || value.includes('"'))
          ) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value || '';
        })
        .join(','),
    ),
  ].join('\n');

  // BOM + CSVコンテンツ
  const csvWithBOM = BOM + csvContent;

  // Blobを作成してダウンロード
  const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
